import * as Sentry from '@sentry/react';
import type { PostHog } from 'posthog-js/react';

import type { ResponderGateway } from '@/shared-responder-v2-core';
import {
  buildReportProblemCapturePayload,
  buildReportProblemInput,
  type ReportProblemCaptureMeta,
  type ReportProblemContext,
  SURVEY_REPORT_PROBLEM,
} from '@/shared-ui';
import { containsSensitiveText } from '@/shared-utilities';

interface SubmitReportProblemParams {
  posthog?: Pick<PostHog, 'capture'>;
  reportProblem: ResponderGateway['reportProblem'];
  description: string;
  context: ReportProblemContext;
  meta: ReportProblemCaptureMeta;
}

async function captureReportProblemToSentry(
  description: string,
  context: ReportProblemContext,
  meta: ReportProblemCaptureMeta
) {
  if (containsSensitiveText(description)) return;

  // Replay runs in buffer mode and only uploads on events carrying an
  // exception, so a message-only capture would arrive with no recording
  // attached. Flushing first turns the buffer into a real replay.
  const replay = Sentry.getReplay();

  try {
    await replay?.flush();
  } catch {
    // A failed replay upload must not cost us the report itself.
  }

  Sentry.captureMessage('Report a problem submitted', {
    level: 'warning',
    fingerprint: ['report-problem'],
    tags: {
      feature: 'report-problem',
      projectId: meta.projectId,
      orgId: meta.orgId,
      insightId: meta.insightId,
      replayId: replay?.getReplayId(),
    },
    extra: {
      description,
      questionId: context.questionId,
      messageId: context.messageId,
    },
  });
}

export async function submitReportProblem({
  posthog,
  reportProblem,
  description,
  context,
  meta,
}: SubmitReportProblemParams): Promise<void> {
  posthog?.capture(
    SURVEY_REPORT_PROBLEM,
    buildReportProblemCapturePayload(description, context, meta)
  );

  await captureReportProblemToSentry(description, context, meta);

  try {
    await reportProblem(buildReportProblemInput(description, context, meta));
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}
